<template>
  <tx-dialog v-model="visible" :title="t('personalSettings.title')" width="65%" height="65%">
    <div class="flex flex-col w-full h-full grow">
      <div class="flex border-b border-gray-200 mb-10">
        <div
          v-for="tab in Object.values(settingsTabs)"
          :key="tab.key"
          class="flex-1 flex justify-center items-center py-3 text-lg cursor-pointer font-medium"
          :class="{ 'border-b-2 border-blue-500 text-blue-500': activeTab === tab.key, 'text-gray-500 hover:text-gray-700': activeTab !== tab.key }"
          @click="activeTab = tab.key"
        >
          {{ t(tab.label) }}
        </div>
      </div>
      <div class="flex-1 overflow-auto p-4">
        <!-- General -->
        <div v-show="activeTab === 'general'">
          <div class="flex flex-row flex-wrap">
            <div v-for="setting in settingsList.general" :key="setting.SystemName" class="px-5 basis-1/3 mb-7">
              <form-editor
                v-model="settingsModel.general[setting.SystemName]"
                :attribute="setting"
                :form="settingsModel"
                :required="setting.IsRequired"
                :show-label="true"
                :clearable="true"
                :disabled="setting.ReadOnly"
                @change="markDirtyTab('general')"
              />
            </div>
          </div>
          <div class="px-5">
            <tx-button type="confirm" :text="t('personalSettings.actions.copySavedViews')" :disabled="isOutsideCatalog" width="150px" @click="() => openCopySavedViewDialog()" />
          </div>
          <div v-if="!isOutsideCatalog" class="px-5">
            <summary-charts-container v-model="selectedSummaryCharts" :charts="summaryChartsList" @update:model-value="markDirtyTab('general')" />
          </div>
        </div>

        <!-- Merch -->
        <div v-show="activeTab === 'merch'">
          <div class="flex flex-row flex-wrap w-full overflow-hidden">
            <div v-for="setting in settingsList.merch" :key="setting.SystemName" class="px-5 basis-1/3 mb-7 max-w-full overflow-hidden">
              <div class="w-full max-w-full overflow-hidden">
                <form-editor
                  v-model="settingsModel.merch[setting.SystemName]"
                  :attribute="setting"
                  :form="settingsModel"
                  :required="setting.IsRequired"
                  :show-label="true"
                  :clearable="true"
                  :disabled="setting.ReadOnly"
                  @change="markDirtyTab('merch')"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Whiteboard -->
        <div v-show="activeTab === 'whiteboard'">
          <div class="flex flex-row flex-wrap">
            <div v-for="setting in settingsList.whiteboard" :key="setting.SystemName" class="px-5 basis-1/3 mb-7">
              <form-editor
                v-model="settingsModel.whiteboard[setting.SystemName]"
                :attribute="setting"
                :form="settingsModel"
                :required="setting.IsRequired"
                :show-label="true"
                :clearable="true"
                :disabled="setting.ReadOnly"
                @change="markDirtyTab('whiteboard')"
              />
            </div>
          </div>
          <div v-if="!isOutsideCatalog" class="px-5">
            <summary-charts-container v-model="selectedWhiteboardSummaryCharts" :charts="whiteboardSummaryCharts" @update:model-value="markDirtyTab('whiteboard')" />
          </div>
        </div>

        <!-- Notifications -->
        <div v-show="activeTab === 'notifications'">
          <div class="flex flex-row flex-wrap">
            <div v-for="setting in notificationSettings" :key="setting.SystemName" class="px-5 basis-1/3 mb-7 overflow-x-hidden overflow-y-auto">
              <form-editor
                v-model="notificationsModel[setting.SystemName]"
                :attribute="setting"
                :form="settingsModel"
                @change="markDirtyTab('notifications')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div>
          <tx-button type="cancel" :text="t('personalSettings.actions.resetToDefault')" width="150px" @click="resetToDefault" @reset="reset" @reset-and-save="resetAndSave" />
        </div>
        <div class="flex space-x-3">
          <tx-button type="cancel" :text="t('general.cancel')" width="150px" @click="cancel" />
          <tx-button type="confirm" :text="t('general.save')" :disabled="showLoading || !dirtyTabs.length" width="150px" @click="() => saveUserPreferences()" />
        </div>
      </div>
    </template>
  </tx-dialog>
  <!-- Reset Dialog -->
  <reset-to-default-dialog ref="resetDialogRef" @reset="reset" @reset-and-save="resetAndSave" />
  <!-- Copy Saved Views Dialog -->
  <copy-saved-views-dialog ref="copySavedViewsDialogRef" />
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isArray } from 'lodash-es'
import { useRoute } from 'vue-router'
import type { ISubscribeNotification } from '../admin/type'
import TxDialog from '@/shared/components/TxDialog.vue'
import FormEditor from '@/shared/components/FormEditor.vue'
import { appConstants, merchConstants, personalSettingsConstants, thumbnailConstants, whiteboardConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import { AttributeType } from '@/models/catalogAttribute'
import utils from '@/services/utils'
import { getNotificationTypes, subscribeNotifications } from '@/api/t1/notification'
import TxButton from '@/shared/components/TxButton.vue'
import ResetToDefaultDialog from '@/modules/personalSettings/ResetToDefaultDialog.vue'
import SummaryChartsContainer from '@/modules/personalSettings/SummaryChartsContainer.vue'
import { createContainer, getContainer, getMyContainers, updateContainer } from '@/api/t1/container'
import appConfig from '@/services/appConfig'
import CopySavedViewsDialog from '@/modules/personalSettings/CopySavedViewsDialog.vue'
import { getSummaryCharts } from '@/shared/components/summaryDialog/utils'
import type { ISummaryChart } from '@/shared/components/summaryDialog/SummaryDialog.types'

const { t } = useI18n()
const userStore = useUserStore()
const route = useRoute()

const visible = ref(false)
const notificationSettings = ref<IFormEditorAttribute[]>([])
const resetDialogRef = ref<InstanceType<typeof ResetToDefaultDialog>>()
const copySavedViewsDialogRef = ref<InstanceType<typeof CopySavedViewsDialog>>()

const settingsModel = reactive({
  general: {} as Record<string, any>,
  merch: {} as Record<string, any>,
  whiteboard: {} as Record<string, any>,
})

const settingsTabs = {
  general: { key: 'general', label: 'personalSettings.tabs.general', visible: true },
  merch: { key: 'merch', label: 'personalSettings.tabs.merch', visible: true },
  whiteboard: { key: 'whiteboard', label: 'personalSettings.tabs.whiteboard', visible: true },
  notifications: { key: 'notifications', label: 'personalSettings.tabs.notifications', visible: true },
}
const activeTab = ref(settingsTabs.general.key)
const dirtyTabs = ref<string[]>([])
const showLoading = ref(false)
const selectedSummaryCharts = ref<string[]>([])
const selectedWhiteboardSummaryCharts = ref<string[]>([])
const slideImageSizes = [
  { label: t('general.large'), value: merchConstants.slideImageDefaultScaleFactors.large },
  { label: t('general.medium'), value: merchConstants.slideImageDefaultScaleFactors.medium },
  { label: t('general.small'), value: merchConstants.slideImageDefaultScaleFactors.small },
]
const slideSizes = [
  { label: t('merch.slideSizes.A4'), value: merchConstants.slideSize.A4 },
  { label: t('merch.slideSizes.A3'), value: merchConstants.slideSize.A3 },
  { label: t('merch.slideSizes.widescreen'), value: merchConstants.slideSize.wideScreen },
]
const thumbnailSizes = [
  { label: t('general.small'), value: 'small' },
  { label: t('general.medium'), value: 'medium' },
  { label: t('general.large'), value: 'large' },
]

const notifications = ref<any[]>([])
const notificationsModel = ref<Record<string, boolean>>({})
// if dynamicConfig, defaultfont config we will set that as the default font
const defaultMerchFont = utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.merchDefaultFont) && appConfig.dynamicConfig.merchDefaultFont.length ? appConfig.dynamicConfig.merchDefaultFont : 'Times New Roman'
const defaultWhiteboardFont = utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.whiteboardDefaultFont) && appConfig.dynamicConfig.whiteboardDefaultFont.length ? appConfig.dynamicConfig.whiteboardDefaultFont : 'Helvetica'

const availableAttributes = computed(() => {
  const result: Record<string, IMyAttribute> = {}
  if (userStore.myAttributes) {
    for (const attributeSystemName in userStore.myAttributes) {
      result[attributeSystemName] = userStore.myAttributes[attributeSystemName]
    }
    result._Segmentation = appConstants.staticAttributes._Segmentations
    result._Color = appConstants.staticAttributes.ColorId
    if (userStore.activeCatalog && userStore.activeCatalog.RequestsEnabled) {
      result._requestSource = appConstants.staticAttributes._RequestSource
      result._requestState = appConstants.staticAttributes._RequestState
      result._requestRejectionReason = appConstants.staticAttributes._RequestReason
    }
  }
  return result
})

const isOutsideCatalog = computed(() => route.name === 'CatalogsList')

const summaryChartsList = computed(() => {
  let charts: ISummaryChart[] = []
  if (userStore.activeCatalog) {
    charts = getSummaryCharts(userStore.activeCatalog.Config.ArticleSummaryReport, availableAttributes.value, userStore.priceGroups)
  }
  return charts.map(c => (Object.assign(
    {},
    appConstants.staticFieldTemplate,
    {
      SystemName: c.id,
      DisplayName: c.title,
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: c,
      ReadOnly: false,
      Creatable: false,
      IsRequired: false,
      Visible: true,
    },
  )))
})

const whiteboardSummaryCharts = computed(() => {
  let charts: ISummaryChart[] = []
  if (userStore.activeCatalog) {
    charts = getSummaryCharts(userStore.activeCatalog.Config.WhiteboardSummaryReport, availableAttributes.value, userStore.priceGroups)
  }
  return charts.map(c => (Object.assign(
    {},
    appConstants.staticFieldTemplate,
    {
      SystemName: c.id,
      DisplayName: c.title,
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: c,
      ReadOnly: false,
      Creatable: false,
      IsRequired: false,
      Visible: true,
    },
  )))
})

const getAttributes = computed(() => {
  if (isOutsideCatalog.value) { return [] }
  if (userStore.activeCatalog && userStore.myAttributes) {
    return Object.values(userStore.myAttributes)
  }
  else {
    return []
  }
})
const getMerchFonts = computed(() => {
  const customFonts = utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.customFonts) && appConfig.dynamicConfig.customFonts.length ? appConfig.dynamicConfig.customFonts : []
  return customFonts.concat(merchConstants.defaultFontFamily)
})
const getWhiteboardFonts = computed(() => {
  const customFonts = utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.customFonts) && appConfig.dynamicConfig.customFonts.length ? appConfig.dynamicConfig.customFonts : []
  return customFonts.concat(whiteboardConstants.defaultFontFamily)
})

// initialize with default values
const settingsList = reactive({
  general: [
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'thumbnailSize',
      DisplayName: t('personalSettings.settings.thumbnailSize'),
      AttributeType: AttributeType.Nvarchar,
      Filterable: true,
      DefaultValue: 'large',
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      DropDownData: thumbnailSizes,
      DropDownValueProp: 'value',
      DropDownValueDisplayProp: 'label',
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsAvailableColors',
      DisplayName: t('personalSettings.settings.showAvailableColorsInArticleDetails'),
      AttributeType: AttributeType.Bool,
      DefaultValue: true,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'showFavoriteIndicators',
      DisplayName: t('personalSettings.settings.showFavoriteIndicators'),
      AttributeType: AttributeType.Bool,
      DefaultValue: true,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
  ],
  // merch
  merch: [
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'slideSize',
      DisplayName: t('merch.dialog.addSlide.fields.slideSize'),
      Creatable: true,
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: merchConstants.slideSize.wideScreen,
      IsRequired: false,
      DropDownData: slideSizes,
      DropDownValueProp: 'value',
      DropDownValueDisplayProp: 'label',
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'imageScaleFactor',
      DisplayName: t('merch.dialog.addSlide.fields.imageSize'),
      DefaultValue: merchConstants.slideImageDefaultScaleFactors.large,
      AttributeType: AttributeType.Nvarchar,
      IsRequired: false,
      DropDownData: slideImageSizes,
      DropDownValueProp: 'value',
      DropDownValueDisplayProp: 'label',
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsAttributeList',
      DisplayName: t('personalSettings.settings.articleDetailsAttributeList'),
      AttributeType: AttributeType.MultiValue,
      DefaultValue: '',
      Multiple: true,
      Visible: true,
      ReadOnly: isOutsideCatalog.value,
      Creatable: true,
      IsRequired: false,
      DropDownData: getAttributes,
      DropDownValueProp: 'SystemName',
      DropDownValueDisplayProp: 'DisplayName',
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsPosition',
      DisplayName: t('personalSettings.settings.articleDetailsPosition'),
      AttributeType: AttributeType.Int,
      DefaultValue: 'bottomCenter',
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      DropDownData: merchConstants.availablePositions,
      DropDownValueProp: 'key',
      DropDownValueDisplayProp: 'value',
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsFont',
      DisplayName: t('personalSettings.settings.articleDetailsFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultMerchFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getMerchFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsFontSize',
      DisplayName: t('personalSettings.settings.articleDetailsFontSize'),
      AttributeType: AttributeType.Int,
      DefaultValue: 11,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsFontColor',
      DisplayName: t('personalSettings.settings.articleDetailsFontColor'),
      AttributeType: AttributeType.ColorPicker,
      DefaultValue: '#000000',
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'modelDetailsPosition',
      DisplayName: t('personalSettings.settings.modelDetailsPosition'),
      AttributeType: AttributeType.Int,
      DefaultValue: 'bottomCenter',
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      DropDownData: merchConstants.availablePositions,
      DropDownValueProp: 'key',
      DropDownValueDisplayProp: 'value',
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'modelDetailsFont',
      DisplayName: t('personalSettings.settings.modelDetailsFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultMerchFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getMerchFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'modelDetailsFontSize',
      DisplayName: t('personalSettings.settings.modelDetailsFontSize'),
      AttributeType: AttributeType.Int,
      DefaultValue: 11,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'modelDetailsFontColor',
      DisplayName: t('personalSettings.settings.modelDetailsFontColor'),
      AttributeType: AttributeType.ColorPicker,
      DefaultValue: '#000000',
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'textBoxFont',
      DisplayName: t('personalSettings.settings.textBoxFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultMerchFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getMerchFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'textBoxFontSize',
      DisplayName: t('personalSettings.settings.textBoxFontSize'),
      AttributeType: AttributeType.Int,
      DefaultValue: 11,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'textBoxFontColor',
      DisplayName: t('personalSettings.settings.textBoxFontColor'),
      AttributeType: AttributeType.ColorPicker,
      DefaultValue: '#000000',
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
  ],
  // Whiteboard
  whiteboard: [
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleDetailsFont',
      DisplayName: t('personalSettings.settings.articleDetailsFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultWhiteboardFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getWhiteboardFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'articleNoImageFont',
      DisplayName: t('personalSettings.settings.articleNoImageFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultWhiteboardFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getWhiteboardFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'modelDetailsFont',
      DisplayName: t('personalSettings.settings.modelDetailsFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultWhiteboardFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getWhiteboardFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'textBoxFont',
      DisplayName: t('personalSettings.settings.textBoxFont'),
      AttributeType: AttributeType.Nvarchar,
      DefaultValue: defaultWhiteboardFont,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
      VettingList: getWhiteboardFonts,
    }),
    Object.assign({}, appConstants.staticFieldTemplate, {
      SystemName: 'zoomSensitivity',
      DisplayName: t('personalSettings.settings.zoomSensitivity'),
      AttributeType: AttributeType.Decimal,
      DropDownData: [
        { label: t('personalSettings.settings.zoomSensitivityVeryLow'), value: 99 },
        { label: t('personalSettings.settings.zoomSensitivityLow'), value: 95 },
        { label: t('personalSettings.settings.zoomSensitivityMedium'), value: 90 },
        { label: t('personalSettings.settings.zoomSensitivityHigh'), value: 85 },
        { label: t('personalSettings.settings.zoomSensitivityVeryHigh'), value: 81 },
      ],
      DropDownValueDisplayProp: 'label',
      DropDownValueProp: 'value',
      DefaultValue: 11,
      Multiple: false,
      Visible: true,
      ReadOnly: false,
      Creatable: true,
      IsRequired: false,
    }),
  ],
})

async function loadNotificationSettings() {
  try {
    const notificationsData = await getNotificationTypes()
    // currently the supported types are Approve Order, Shared Slides, Shared Tags
    const allowedTypes = ['Approved Order', 'Shared Slides', 'Shared Tags']

    notifications.value = notificationsData.data.filter(item =>
      allowedTypes.includes(item.Type),
    )

    notificationSettings.value = notifications.value.map(item =>
      Object.assign({}, appConstants.staticFieldTemplate, {
        SystemName: item.Id,
        DisplayName: item.Type,
        AttributeType: AttributeType.Bool,
        DefaultValue: item.Status === 1,
        Multiple: false,
        Visible: true,
        ReadOnly: false,
        Creatable: true,
        IsRequired: false,
      }),
    )

    const newModel: Record<string, boolean> = {}

    userStore.userProfile.NotificationSubscribeTypes.forEach((sub) => {
      newModel[sub.TypeId] = sub.Status === 1
    })

    notificationsModel.value = newModel
  }
  catch (error) {
    console.error('Failed to load notification settings:', error)
  }
}

function cancel() {
  visible.value = false
}

function resetToDefault() {
  resetDialogRef.value?.show()
}

function reset(tabsToReset: string[]) {
  tabsToReset.forEach((tab) => {
    dirtyTabs.value.push(tab)
    setDefaultSettings(tab)
  })
}

function setDefaultSettings(tab: string) {
  if (tab === 'general' || tab === 'merch') {
    settingsModel[tab] = {}
    settingsList[tab].forEach((setting) => {
      settingsModel[tab][setting.SystemName] = setting.DefaultValue
    })

    if (tab === 'general' && !isOutsideCatalog.value) {
      selectedSummaryCharts.value = summaryChartsList.value.map(chart => chart.SystemName)
    }
  }
  else if (tab === 'whiteboard') {
    selectedWhiteboardSummaryCharts.value = whiteboardSummaryCharts.value.map(chart => chart.SystemName)
  }
  else if (tab === 'notifications') {
    const newModel = { ...notificationsModel.value }

    notifications.value.forEach((notification) => {
      newModel[notification.Id] = notification.AutoSubscribe === 1
    })

    notificationsModel.value = newModel
  }
}

async function resetAndSave(tabsToReset: string[]) {
  reset(tabsToReset)
  await saveUserPreferences(true)
}

async function saveUserPreferences(_resetAndSave: boolean = false) {
  showLoading.value = true
  const tabsToSave = dirtyTabs.value
  const updatedTabSettings: Record<string, any> = {}

  try {
    for (const tab of tabsToSave) {
      if (tab === 'notifications') {
        try {
          const subscribeList: ISubscribeNotification[] = Object.entries(notificationsModel.value)
            .filter(([_, value]) => value) // only active onces
            .map(([key]) => ({ TypeId: key }))

          await subscribeNotifications(subscribeList)
          userStore.setProfile()
        }
        catch (error) {
          console.error('Failed to save notifications:', error)
        }
      }
      else {
        const modelSettings = { ...settingsModel[tab] }

        // catalog dependent settings
        if (!isOutsideCatalog.value && userStore.activeCatalog) {
          if (modelSettings.articleDetailsAttributeList) {
            modelSettings.articleDetailsAttributeList = {
              [userStore.activeCatalog.CatalogCode]: modelSettings.articleDetailsAttributeList,
            }
          }

          if (tab === 'whiteboard') {
            modelSettings.whiteboardSummaryCharts = {
              [userStore.activeCatalog.CatalogCode]: selectedWhiteboardSummaryCharts.value || [],
            }
          }
          else {
            modelSettings.summaryCharts = {
              [userStore.activeCatalog.CatalogCode]: selectedSummaryCharts.value || [],
            }
          }
        }

        updatedTabSettings[tab] = modelSettings
      }
    }

    const containersList = await utils.tryAsync(getMyContainers())
    if (containersList.success) {
      const container = containersList.result.data.find(f => f.ContainerName === personalSettingsConstants.settings)

      if (!container) {
        const requestData = {
          version: 1,
          serverVersion: 1,
          userPreferences: updatedTabSettings,
        }
        await utils.tryAsync(createContainer(userStore.userProfile.Id, personalSettingsConstants.settings, JSON.stringify(requestData)))
        userStore.settingsContainer = updatedTabSettings
        if (utils.isDefined(updatedTabSettings.general.thumbnailSize)) {
          userStore.setThumbnailSize(thumbnailConstants.thumbnailSizeStrToNumberEnum[updatedTabSettings.general.thumbnailSize])
        }
      }
      else {
        const res = await utils.tryAsync(getContainer(container.Id))
        if (res.success) {
          const existingData = utils.tryParse(res.result.data.Value) || {}

          existingData.data = existingData.data || {}
          existingData.data.userPreferences = {
            ...(existingData.data.userPreferences || {}),
            ...updatedTabSettings,
          }

          await updateContainer(userStore.userProfile.Id, res.result.data.Id, personalSettingsConstants.settings, JSON.stringify(existingData))
          userStore.settingsContainer = existingData.data.userPreferences
          if (utils.isDefined(updatedTabSettings.general.thumbnailSize)) {
            userStore.setThumbnailSize(thumbnailConstants.thumbnailSizeStrToNumberEnum[updatedTabSettings.general.thumbnailSize])
          }
        }
      }
    }
  }
  catch (err) {
    console.error('Failed to save settings:', err)
  }
  finally {
    visible.value = false
    showLoading.value = false
  }
}

async function showDialog() {
  visible.value = true
  showLoading.value = true
  try {
    await loadNotificationSettings()
    cleanData()
    dirtyTabs.value = []
  }
  finally {
    showLoading.value = false
  }
}

function cleanData() {
  const catalogCode = userStore.activeCatalog?.CatalogCode
  settingsModel.general = {}
  settingsModel.merch = {}
  settingsModel.whiteboard = {}

  for (const key in settingsList) {
    settingsList[key].forEach((setting) => {
      settingsModel[key][setting.SystemName] = setting.DefaultValue
    })
  }

  if (userStore.settingsContainer) {
    try {
      const storedSettings = userStore.settingsContainer

      if (storedSettings.general) {
        settingsModel.general = { ...settingsModel.general, ...storedSettings.general }

        if (catalogCode && storedSettings.general.summaryCharts?.[catalogCode]) {
          selectedSummaryCharts.value = storedSettings.general.summaryCharts[catalogCode]
        }
        else if (isArray(storedSettings.general.summaryCharts)) {
          selectedSummaryCharts.value = storedSettings.general.summaryCharts
        }
      }

      if (storedSettings.merch) {
        settingsModel.merch = { ...settingsModel.merch, ...storedSettings.merch }

        if (catalogCode && storedSettings.merch.articleDetailsAttributeList?.[catalogCode]) {
          settingsModel.merch.articleDetailsAttributeList = storedSettings.merch.articleDetailsAttributeList[catalogCode]
        }
        else {
          settingsModel.merch.articleDetailsAttributeList = []
        }
      }

      if (storedSettings.whiteboard) {
        settingsModel.whiteboard = { ...settingsModel.whiteboard, ...storedSettings.whiteboard }
        if (catalogCode && storedSettings.whiteboard.whiteboardSummaryCharts?.[catalogCode]) {
          selectedWhiteboardSummaryCharts.value = storedSettings.whiteboard.whiteboardSummaryCharts[catalogCode]
        }
        else if (isArray(storedSettings.whiteboard.whiteboardSummaryCharts)) {
          selectedWhiteboardSummaryCharts.value = storedSettings.whiteboard.whiteboardSummaryCharts
        }
      }
    }
    catch (e) {
      console.warn('Failed to set stored settings', e)
    }
  }

  if (!isOutsideCatalog.value) {
    if (!selectedSummaryCharts.value || selectedSummaryCharts.value.length === 0) {
      selectedSummaryCharts.value = summaryChartsList.value.map(chart => chart.SystemName)
    }
    if (!selectedWhiteboardSummaryCharts.value || selectedWhiteboardSummaryCharts.value.length === 0) {
      selectedWhiteboardSummaryCharts.value = whiteboardSummaryCharts.value.map(chart => chart.SystemName)
    }
  }

  dirtyTabs.value = []
}

function openCopySavedViewDialog() {
  copySavedViewsDialogRef.value?.show()
}

function markDirtyTab(tab: string) {
  if (!dirtyTabs.value.includes(tab)) {
    dirtyTabs.value.push(tab)
  }
}

defineExpose({
  showDialog,
})
</script>
